import { Sparkles } from 'lucide-react';
import * as React from 'react';

import { Button } from '../../components-ui/Button';
import { cn } from '../../lib/utils';
import { isPresenterMode } from '../../utils/isPresenterMode';
import type { Feature } from '../features';
import useOrganizationStore from '../../store/organization';
import { INTEREST_CTA_LABEL, neutralUpsellCopyFor, upsellCopyFor } from './copy';
import { useUpsell } from './UpsellProvider';

type Props = {
  feature: Feature;
  /** Overrides the copy registry when a surface needs to be more specific. */
  title?: string;
  description?: string;
  className?: string;
};

/**
 * The in-panel upsell: an icon, a statement of what the capability does, and one way to
 * reach a human. Forked from `PerformanceEmptyState` so a locked panel and an empty panel
 * feel like the same product rather than two different design languages.
 *
 * Renders nothing in presenter mode — see `useUpsellSuppressed`.
 */
export function UpsellCard({ feature, title, description, className }: Props) {
  const openUpsell = useUpsell();
  // Same gate as `UpsellDialog`, and this is the surface that reaches the client FIRST: four of
  // `copy.ts`'s titles name the vendor, so a white-label tenant was reading our name unprompted.
  // `null` is "not known yet" (`../tenant.ts`) and takes the vendor-free copy, true on both.
  const isNexoyaTenant = useOrganizationStore((s) => s.tenantBrand) === 'nexoya';
  const copy = isNexoyaTenant ? upsellCopyFor(feature) : neutralUpsellCopyFor(feature);
  const suppressed = useUpsellSuppressed();

  if (suppressed) return null;

  return (
    <div
      className={cn(
        'pointer-events-auto flex w-[400px] max-w-[88%] flex-col items-center gap-4 rounded-2xl border border-neutral-100 bg-white px-8 py-7 text-center shadow-[0_8px_28px_-12px_rgba(42,42,50,0.18)]',
        className,
      )}
      role="status"
    >
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-green-400/10" aria-hidden>
        <Sparkles className="h-6 w-6 text-green-400" strokeWidth={2.25} />
      </span>

      <div className="flex flex-col gap-1.5">
        <h3 className="text-base font-semibold tracking-tight text-neutral-800">{title ?? copy.title}</h3>
        <p className="mx-auto max-w-[320px] text-sm font-medium leading-snug text-neutral-500">
          {description ?? copy.description}
        </p>
      </div>

      {/* Always rendered, and the label never forks: it opens an internal dialog and names
          neither vendor nor tier, so it is true on every tenant including an unresolved one. */}
      <Button
        type="button"
        variant="success"
        onClick={() => openUpsell(feature)}
        className="mt-1 h-9 px-6 font-semibold shadow-sm"
      >
        {INTEREST_CTA_LABEL}
      </Button>
    </div>
  );
}

/**
 * Upsell chrome must not appear in anything the agency shows its own client. The
 * `?presenter_mode=true` URL toggle is exactly that surface — and ONLY that surface.
 *
 * Deliberately reads `isPresenterMode()` rather than the `usePresenterMode()` hook: the
 * hook also returns `true` for `{role:readonly}`, an ordinary assignable seat held by most
 * analysts at an agency. Suppressing on it would leave every real Read-Only user on a
 * Nexoya Starter team looking at an empty panel — no upsell, no message, no error.
 * `PrivateApp.tsx` already draws this exact distinction for the sidebar.
 *
 * Kept a hook even though it wraps a plain function, so call sites (`UpsellTeaser`,
 * `PlanTabLabel`) do not change and it stays a single mockable seam.
 */
export function useUpsellSuppressed(): boolean {
  return isPresenterMode();
}

export default UpsellCard;
